import { useEffect, useState } from 'react';
import { api, type BurnSeries, type MeBudget, type PeerInfo } from '../lib/api';
import { BurnRateSparkline } from '../components/BurnRateSparkline';
import { Card } from '../components/Card';
import { TabularNumber } from '../components/TabularNumber';
import { fmtPct, fmtRelative, fmtUsd, microsToUsd, centsToUsd } from '../lib/format';

interface State {
  loading: boolean;
  budget: MeBudget | null;
  series: BurnSeries | null;
  peer: PeerInfo | null;
  error: string | null;
}

const REFRESH_MS = 30_000;

export function EmployeeDashboard() {
  const [s, setS] = useState<State>({ loading: true, budget: null, series: null, peer: null, error: null });

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;

    async function load(): Promise<void> {
      try {
        const [budget, series, peer] = await Promise.all([
          api.get<{ budget_id: string } & MeBudget | { budget: null }>('/v1/me/budget'),
          api.get<BurnSeries>('/v1/me/burn?bucket=day'),
          api.get<PeerInfo>('/v1/me/peer'),
        ]);
        if (cancelled) return;
        const b = 'budget' in budget && budget.budget === null ? null : (budget as MeBudget);
        setS({ loading: false, budget: b, series, peer, error: null });
      } catch (e) {
        if (cancelled) return;
        setS((prev) => ({ ...prev, loading: false, error: String(e) }));
      }
    }
    void load();
    timer = window.setInterval(load, REFRESH_MS);
    return () => { cancelled = true; if (timer) window.clearInterval(timer); };
  }, []);

  if (s.loading) return <DashboardSkeleton />;
  if (s.error) return <div className="p-8 text-[color:var(--alarm)]">Error: {s.error}</div>;
  if (!s.budget) return <NoBudget />;

  const { budget, series, peer } = s;
  return (
    <div className="max-w-6xl mx-auto px-6 py-10 space-y-8">
      {(budget.over_budget || budget.status === 'paused') && (
        <div className="bg-[color:var(--alarm)] text-[color:var(--ink-0)] px-6 py-4 rounded font-semibold text-center">
          BUDGET EXCEEDED — PAUSED · {fmtUsd(microsToUsd(budget.consumed_micros))} consumed of {fmtUsd(centsToUsd(budget.allocated_cents))} allocated
        </div>
      )}
      <header className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl tracking-tight">Your token budget</h1>
          <p className="text-sm text-[color:var(--mute)] mt-1">
            Period ends {new Date(budget.period_end).toLocaleDateString()} · last event {budget.last_event_at ? fmtRelative(budget.last_event_at) : '—'}
          </p>
        </div>
        <a href="/settings" className="text-xs uppercase tracking-widest text-[color:var(--mute)] hover:text-[color:var(--paper)]">
          Settings →
        </a>
      </header>

      <BurnRateSparkline
        points={series?.points ?? []}
        allocatedCents={budget.allocated_cents}
        periodStart={budget.period_start}
        periodEnd={budget.period_end}
        consumedMicros={budget.consumed_micros}
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card title="Remaining">
          <TabularNumber size="xl">{fmtUsd(microsToUsd(budget.remaining_micros))}</TabularNumber>
          <div className="text-sm text-[color:var(--mute)] mt-2">of {fmtUsd(centsToUsd(budget.allocated_cents))} allocated</div>
        </Card>
        <Card title="Utilization">
          <TabularNumber size="xl">{fmtPct(budget.utilization_pct)}</TabularNumber>
          <div className="text-sm text-[color:var(--mute)] mt-2">{budget.events_count.toLocaleString()} events recorded</div>
        </Card>
        <Card title="Peer percentile">
          {peer && peer.percentile !== null ? (
            <>
              <TabularNumber size="xl">p{peer.percentile}</TabularNumber>
              <div className="text-sm text-[color:var(--mute)] mt-2">
                {peer.role}{peer.level ? ` · ${peer.level}` : ''} · cohort {peer.cohort_size}
              </div>
            </>
          ) : (
            <div className="text-sm text-[color:var(--mute)]">No cohort data yet</div>
          )}
        </Card>
      </div>
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="max-w-6xl mx-auto px-6 py-10 space-y-8">
      <div className="h-8 w-64 bg-[color:var(--ink-1)] rounded" />
      <div className="h-72 bg-[color:var(--ink-1)] rounded" />
      <div className="grid grid-cols-3 gap-6">
        {[0, 1, 2].map((i) => <div key={i} className="h-32 bg-[color:var(--ink-1)] rounded" />)}
      </div>
    </div>
  );
}

function NoBudget() {
  return (
    <div className="max-w-2xl mx-auto px-6 py-20 text-center">
      <h1 className="text-2xl mb-4">No active budget</h1>
      <p className="text-[color:var(--mute)]">Your administrator hasn't allocated a token budget for the current period yet. Once they do, your burn rate, allowance, and peer percentile will appear here.</p>
    </div>
  );
}
